import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { create as createApi, auth } from "@/lib/api";
import { fmtAmt, fmtDate } from "@/lib/flags";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import WorkspaceSwitcher from "@/components/WorkspaceSwitcher";
import MyPRDetailModal from "@/components/MyPRDetailModal";
import PrSearchBox from "@/components/PrSearchBox";
import UserMenu from "@/components/UserMenu";
import { toast } from "sonner";
import {
  Plus, RefreshCw, FileText, ShieldCheck, CheckCircle2, XCircle,
  PauseCircle, Clock, BadgeCheck, Search, Filter, AlertTriangle, Copy, Loader2,
} from "lucide-react";

const STATUS_OPTS = ["All", "Pending Authoriser", "Authorised — awaiting Approver", "Approved", "Rejected", "On Hold"];

function StatusPill({ label }) {
  let cls = "bg-slate-100 text-slate-700 border-slate-300";
  let Icon = Clock;
  if (label?.startsWith("Approved")) { cls = "bg-emerald-100 text-emerald-800 border-emerald-200"; Icon = CheckCircle2; }
  else if (label?.startsWith("Rejected")) { cls = "bg-rose-100 text-rose-800 border-rose-200"; Icon = XCircle; }
  else if (label === "On Hold" || label === "Hold") { cls = "bg-yellow-100 text-yellow-900 border-yellow-300"; Icon = PauseCircle; }
  else if (label?.includes("Authorised")) { cls = "bg-indigo-100 text-indigo-800 border-indigo-200"; Icon = BadgeCheck; }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-semibold border ${cls}`}>
      <Icon size={11} /> {label}
    </span>
  );
}

export default function MyPRs() {
  const user = auth.user();
  const role = user?.role || "";
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("All");
  const [openId, setOpenId] = useState(null);
  const [openStatus, setOpenStatus] = useState("");
  const [cloning, setCloning] = useState(null);

  const cloneToDraft = async (vocNo) => {
    setCloning(vocNo);
    try {
      const { data } = await createApi.cloneRejected(vocNo);
      toast.success(`Cloned PR #${vocNo} into a new draft — opening editor…`);
      nav(`/create?draft=${data.draft_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    } finally {
      setCloning(null);
    }
  };

  const dismissPR = async (vocNo) => {
    if (!window.confirm(
      `Hide PR #${vocNo} from your list?\n\nIt will be removed from your view, but the rejection stays in the audit trail. ` +
      `Toggle "Show hidden" above to bring it back.`
    )) return;
    try {
      await createApi.dismissMyPR(vocNo);
      toast.success(`PR #${vocNo} dismissed from your list`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not dismiss PR");
    }
  };

  const restorePR = async (vocNo) => {
    try {
      await createApi.restoreMyPR(vocNo);
      toast.success(`PR #${vocNo} restored to your list`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not restore PR");
    }
  };

  const [showHidden, setShowHidden] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await createApi.myPRs({ include_dismissed: showHidden ? "true" : "false" });
      setRows(data || []);
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to load your PRs"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [showHidden]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      if (status !== "All" && r.status_label !== status) return false;
      if (q) {
        const s = q.toLowerCase();
        if (!String(r.voc_no).includes(s) && !(r.supplier || "").toLowerCase().includes(s) && !(r.pcode || "").toLowerCase().includes(s))
          return false;
      }
      return true;
    });
  }, [rows, status, q]);

  const stats = useMemo(() => {
    const s = { total: rows.length, pending: 0, approved: 0, rejected: 0, hold: 0 };
    for (const r of rows) {
      if (r.status_label?.startsWith("Approved")) s.approved++;
      else if (r.status_label?.startsWith("Rejected")) s.rejected++;
      else if (r.status_label?.includes("On Hold") || r.status_label === "Hold") s.hold++;
      else s.pending++;
    }
    return s;
  }, [rows]);

  const rejectedRows = useMemo(() => rows.filter((r) => r.rejection), [rows]);
  const heldRows = useMemo(() => rows.filter((r) => r.hold && !r.rejection), [rows]);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="h-16 bg-cyan-700 text-white sticky top-0 z-40 shadow-sm">
        <div className="max-w-[1400px] mx-auto h-full px-3 sm:px-6 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <ShieldCheck size={18} />
            </div>
            <div className="min-w-0">
              <div className="text-lg sm:text-xl font-bold tracking-tight leading-none truncate">My PRs</div>
              <div className="text-[10px] sm:text-[11px] text-cyan-100 mt-1 tracking-wider uppercase">Creator workspace</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <PrSearchBox onFound={(vn, data) => { setOpenId(vn); setOpenStatus(data?.req_status || ""); }} variant="header" />
            <WorkspaceSwitcher role={role} />
            <UserMenu tone="dark" />
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto p-3 sm:p-6 space-y-4 sm:space-y-5">
        {/* CTA + KPI strip */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 sm:gap-4">
          <button
            onClick={() => nav("/create")}
            data-testid="new-pr-cta"
            className="bg-gradient-to-br from-cyan-700 to-cyan-900 text-white rounded-sm p-5 text-left hover:shadow-xl hover:-translate-y-0.5 transition-all"
          >
            <Plus size={22} className="mb-2" />
            <div className="text-sm font-bold">Create New PR</div>
            <div className="text-[11px] text-cyan-100 mt-0.5">Live AI pre-check before submit</div>
          </button>
          <Stat label="My PRs" value={stats.total} icon={FileText} accent="slate" />
          <Stat label="Pending" value={stats.pending} icon={Clock} accent="indigo" />
          <Stat label="Approved" value={stats.approved} icon={CheckCircle2} accent="emerald" />
          <Stat label="Rejected / Hold" value={stats.rejected + stats.hold} icon={XCircle} accent="rose" />
        </div>

        {/* Filter bar */}
        <div className="bg-white border border-slate-200 rounded-sm p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-2.5 text-slate-400" />
            <Input
              data-testid="mypr-search"
              placeholder="Search by Voc_no, supplier name, pcode…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-slate-500" />
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="mypr-status-filter" className="w-[230px]"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
            <label className="text-xs text-slate-600 inline-flex items-center gap-1.5 select-none cursor-pointer" data-testid="show-hidden-toggle">
              <input
                type="checkbox"
                checked={showHidden}
                onChange={(e) => setShowHidden(e.target.checked)}
                className="accent-cyan-600"
              />
              Show hidden
            </label>
            <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="mypr-refresh">
              <RefreshCw size={14} className={`mr-1 ${busy ? "animate-spin" : ""}`} />Refresh
            </Button>
          </div>
        </div>

        {/* Needs Attention: rejected PRs with clone-to-draft action */}
        {rejectedRows.length > 0 && (
          <div className="bg-rose-50/60 border border-rose-200 rounded-sm overflow-hidden" data-testid="needs-attention">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-rose-100/70 border-b border-rose-200 text-rose-900">
              <AlertTriangle size={16} />
              <div className="text-sm font-bold uppercase tracking-wider">Needs your attention</div>
              <span className="text-[11px] text-rose-700 ml-auto">{rejectedRows.length} rejected PR{rejectedRows.length === 1 ? "" : "s"}</span>
              <button
                onClick={() => setShowHidden(!showHidden)}
                data-testid="toggle-hidden"
                className="text-[11px] text-rose-700 hover:text-rose-900 underline-offset-2 hover:underline ml-2"
              >
                {showHidden ? "Hide dismissed" : "Show hidden"}
              </button>
            </div>
            <div className="divide-y divide-rose-200/60">
              {rejectedRows.map((r) => (
                <div key={r.voc_no} className={`px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3 ${r.dismissed ? "opacity-60 bg-slate-50" : ""}`} data-testid={`rejected-row-${r.voc_no}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-semibold text-rose-900">PR #{r.voc_no}</span>
                      <span className="text-[10px] uppercase tracking-wider text-rose-700 bg-rose-200/70 px-2 py-0.5 rounded-sm font-bold">{r.rejection.stage}</span>
                      <span className="text-xs text-slate-600">{fmtDate(r.vdate)}</span>
                      <span className="text-xs text-slate-600 truncate">{r.supplier}</span>
                      {r.dismissed && (
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 bg-slate-200 px-2 py-0.5 rounded-sm font-bold">Hidden</span>
                      )}
                    </div>
                    <div className="text-xs text-rose-800 mt-1 italic">{r.rejection.reason || "Rejected"}</div>
                    {r.rejection.by_name && (
                      <div className="text-[11px] text-slate-600 mt-0.5">
                        Rejected by <span className="font-semibold text-slate-800">{r.rejection.by_name}</span>
                        {r.rejection.at && <span className="text-slate-400"> · {r.rejection.at}</span>}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold num text-slate-800">₹{fmtAmt(r.amount)}</div>
                  </div>
                  <div className="flex gap-2 sm:flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setOpenId(r.voc_no); setOpenStatus(r.status_label); }}
                      data-testid={`view-rejected-${r.voc_no}`}
                    >
                      View
                    </Button>
                    {!r.dismissed ? (
                      <>
                        <Button
                          size="sm"
                          onClick={() => cloneToDraft(r.voc_no)}
                          disabled={cloning === r.voc_no}
                          className="bg-rose-600 hover:bg-rose-700 text-white"
                          data-testid={`clone-rejected-${r.voc_no}`}
                        >
                          {cloning === r.voc_no ? <Loader2 size={14} className="animate-spin mr-1" /> : <Copy size={14} className="mr-1" />}
                          Clone to Draft
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => dismissPR(r.voc_no)}
                          data-testid={`dismiss-rejected-${r.voc_no}`}
                          title="Hide from my view (kept in audit)"
                          className="text-slate-600 hover:text-slate-900"
                        >
                          Dismiss
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => restorePR(r.voc_no)}
                        data-testid={`restore-rejected-${r.voc_no}`}
                      >
                        Restore
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* On Hold notification — PRs an approver has parked, awaiting their next look */}
        {heldRows.length > 0 && (
          <div className="bg-amber-50/60 border border-amber-200 rounded-sm overflow-hidden" data-testid="held-notification">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-100/70 border-b border-amber-200 text-amber-900">
              <PauseCircle size={16} />
              <div className="text-sm font-bold uppercase tracking-wider">On hold by an approver</div>
              <span className="text-[11px] text-amber-700 ml-auto">{heldRows.length} PR{heldRows.length === 1 ? "" : "s"} parked</span>
            </div>
            <div className="divide-y divide-amber-200/60">
              {heldRows.map((r) => (
                <div key={r.voc_no} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`held-row-${r.voc_no}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono font-semibold text-amber-900">PR #{r.voc_no}</span>
                      <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-200/70 px-2 py-0.5 rounded-sm font-bold">On Hold</span>
                      <span className="text-xs text-slate-600">{fmtDate(r.vdate)}</span>
                      <span className="text-xs text-slate-600 truncate">{r.supplier}</span>
                    </div>
                    {r.hold.note && (
                      <div className="text-xs text-amber-900 mt-1 italic">&ldquo;{r.hold.note}&rdquo;</div>
                    )}
                    <div className="text-[11px] text-slate-600 mt-0.5">
                      Held by <span className="font-semibold text-slate-800">{r.hold.by_name || r.hold.by_emp_no || "—"}</span>
                      {r.hold.at && <span className="text-slate-400"> · {r.hold.at}</span>}
                      <span className="text-slate-400"> · Auto re-surfaces in approver&apos;s queue after 7 days</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold num text-slate-800">₹{fmtAmt(r.amount)}</div>
                  </div>
                  <div className="flex gap-2 sm:flex-shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setOpenId(r.voc_no); setOpenStatus(r.status_label); }}
                      data-testid={`view-held-${r.voc_no}`}
                    >
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Table (desktop) */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden hidden md:block">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Voc_no</th>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Supplier</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">L1 / L2</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((r) => (
                <tr
                  key={r.voc_no}
                  onClick={() => { setOpenId(r.voc_no); setOpenStatus(r.status_label); }}
                  className="hover:bg-cyan-50/40 cursor-pointer"
                  data-testid={`mypr-row-${r.voc_no}`}
                >
                  <td className="px-4 py-3 font-mono font-semibold text-cyan-800">{r.voc_no}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(r.vdate)}</td>
                  <td className="px-4 py-3 text-slate-600">{r.purc_type}</td>
                  <td className="px-4 py-3">
                    <div className="truncate max-w-[260px]">{r.supplier || r.pcode}</div>
                    <div className="text-[10px] mono text-slate-400">{r.pcode}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold num">₹{fmtAmt(r.amount)}</td>
                  <td className="px-4 py-3"><StatusPill label={r.status_label} /></td>
                  <td className="px-4 py-3 text-[11px] text-slate-600">
                    <div>{r.auth_by ? <span className="inline-flex items-center gap-1"><BadgeCheck size={11} className="text-indigo-600" />{r.auth_by}</span> : <span className="text-slate-400">—</span>}</div>
                    <div>{r.app_by ? <span className="inline-flex items-center gap-1"><CheckCircle2 size={11} className="text-emerald-600" />{r.app_by}</span> : <span className="text-slate-400">—</span>}</div>
                  </td>
                </tr>
              ))}
              {!busy && filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-slate-400 text-sm">
                  No PRs found. {rows.length === 0 ? <button onClick={() => nav("/create")} className="text-cyan-700 underline ml-1" data-testid="empty-create-link">Create your first PR →</button> : "Try adjusting filters."}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Card list (mobile) */}
        <div className="md:hidden space-y-2">
          {filtered.map((r) => (
            <button
              key={r.voc_no}
              onClick={() => { setOpenId(r.voc_no); setOpenStatus(r.status_label); }}
              data-testid={`mypr-card-${r.voc_no}`}
              className="w-full text-left bg-white border border-slate-200 rounded-sm p-3 active:bg-cyan-50"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-mono font-semibold text-cyan-800 text-sm">PR #{r.voc_no}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{fmtDate(r.vdate)} · {r.purc_type}</div>
                </div>
                <div className="text-right">
                  <div className="font-bold num text-sm">₹{fmtAmt(r.amount)}</div>
                </div>
              </div>
              <div className="text-sm text-slate-700 mt-1.5 truncate">{r.supplier || r.pcode}</div>
              <div className="mt-2"><StatusPill label={r.status_label} /></div>
            </button>
          ))}
          {!busy && filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400 text-sm bg-white border border-slate-200 rounded-sm">
              No PRs. {rows.length === 0 && <button onClick={() => nav("/create")} className="text-cyan-700 underline ml-1">Create one</button>}
            </div>
          )}
        </div>
      </main>

      <MyPRDetailModal
        vocNo={openId}
        open={!!openId}
        statusLabel={openStatus}
        onClose={() => setOpenId(null)}
      />
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  const colors = {
    slate: "from-slate-50 to-white text-slate-700 border-slate-200",
    indigo: "from-indigo-50 to-white text-indigo-800 border-indigo-200",
    emerald: "from-emerald-50 to-white text-emerald-800 border-emerald-200",
    rose: "from-rose-50 to-white text-rose-800 border-rose-200",
  }[accent] || "from-slate-50 to-white text-slate-700 border-slate-200";
  return (
    <div className={`bg-gradient-to-br rounded-sm p-4 border ${colors}`}>
      <div className="flex items-center justify-between">
        <Icon size={18} className="opacity-70" />
      </div>
      <div className="text-3xl font-extrabold num mt-2">{value}</div>
      <div className="text-[11px] uppercase tracking-wider opacity-70 mt-0.5">{label}</div>
    </div>
  );
}
